import React from 'react';
import { Smartphone, Globe, Monitor, Workflow, Brain, Users } from 'lucide-react';

interface ServicesProps {
  onNavigateToMobileApps?: () => void;
  onNavigateToDesktopSoftware?: () => void;
  onNavigateToN8NAutomation?: () => void;
  onNavigateToWebApps?: () => void;
  onNavigateToAI?: () => void;
}

const Services: React.FC<ServicesProps> = ({ 
  onNavigateToMobileApps, 
  onNavigateToDesktopSoftware, 
  onNavigateToN8NAutomation, 
  onNavigateToWebApps,
  onNavigateToAI
}) => {
  const services = [
    {
      icon: Smartphone,
      title: "Aplicaciones Móviles",
      description: "Desarrollo nativo y multiplataforma para Android e iOS con interfaces intuitivas y rendimiento optimizado.",
      color: "bg-blue-100 text-blue-600",
      hasDetailPage: true
    },
    {
      icon: Globe,
      title: "Aplicaciones Web Escalables",
      description: "Plataformas web robustas y responsivas diseñadas para crecer con tu negocio y manejar alto tráfico.",
      color: "bg-teal-100 text-teal-600",
      hasDetailPage: true
    },
    {
      icon: Monitor,
      title: "Software de Escritorio",
      description: "Aplicaciones especializadas para procesos específicos con integración total a tu infraestructura existente.",
      color: "bg-purple-100 text-purple-600",
      hasDetailPage: true
    },
    {
      icon: Workflow,
      title: "Automatización con N8N",
      description: "Optimización de procesos empresariales mediante flujos automatizados que reducen tiempos y errores.",
      color: "bg-orange-100 text-orange-600",
      hasDetailPage: true
    },
    {
      icon: Brain,
      title: "Inteligencia Artificial",
      description: "Integración de IA para análisis predictivo, procesamiento de lenguaje natural y toma de decisiones inteligentes.",
      color: "bg-green-100 text-green-600",
      hasDetailPage: true
    },
    {
      icon: Users,
      title: "Consultoría Tecnológica",
      description: "Asesoramiento estratégico para la transformación digital y adopción de nuevas tecnologías en tu organización.",
      color: "bg-red-100 text-red-600",
      hasDetailPage: false
    }
  ];

  const handleServiceClick = (service: typeof services[0]) => {
    if (service.hasDetailPage && service.title === "Aplicaciones Móviles" && onNavigateToMobileApps) {
      onNavigateToMobileApps();
    } else if (service.hasDetailPage && service.title === "Software de Escritorio" && onNavigateToDesktopSoftware) {
      onNavigateToDesktopSoftware();
    } else if (service.hasDetailPage && service.title === "Automatización con N8N" && onNavigateToN8NAutomation) {
      onNavigateToN8NAutomation();
    } else if (service.hasDetailPage && service.title === "Aplicaciones Web Escalables" && onNavigateToWebApps) {
      onNavigateToWebApps();
    } else if (service.hasDetailPage && service.title === "Inteligencia Artificial" && onNavigateToAI) {
      onNavigateToAI();
    }
  };

  return (
    <section id="servicios" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Nuestros <span className="text-blue-600">Servicios</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Soluciones tecnológicas completas diseñadas para impulsar el crecimiento 
            y la eficiencia de tu empresa en la era digital.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group"
              >
                <div className={`w-16 h-16 ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
                <div className="mt-6">
                  <button 
                    onClick={() => handleServiceClick(service)}
                    className="text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-200 group-hover:underline"
                  >
                    {service.hasDetailPage ? 'Ver detalles →' : 'Conoce más →'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-8 rounded-2xl text-white">
            <h3 className="text-2xl font-bold mb-4">
              ¿No encuentras exactamente lo que necesitas?
            </h3>
            <p className="text-lg mb-6 opacity-90">
              Creamos soluciones personalizadas para desafíos únicos de tu industria.
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200">
              Solicita una consulta gratuita
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;